/**
 * Fix Project Customer References After Migration
 * This script updates project.customer_id to use the new JARVIS customer UUIDs
 */

import { directSupabase } from '../lib/supabaseClient';

export async function fixProjectCustomerReferences() {
  console.log('🔗 Starting project customer reference fix...');
  
  try {
    // Step 1: Get all migration mappings
    console.log('📥 Fetching migration mappings...');
    const { data: mappings, error: mappingError } = await directSupabase
      .from('customer_migration_mapping')
      .select('*');
    
    if (mappingError) {
      throw new Error(`Failed to fetch mappings: ${mappingError.message}`);
    }
    
    console.log(`Found ${mappings?.length || 0} customer mappings`);
    
    if (!mappings || mappings.length === 0) {
      console.log('⚠️ No migration mappings found. Migration may not have been run yet.');
      return { success: true, updated: 0, message: 'No mappings to process' };
    }
    
    // Step 2: Get all projects with customer_id values
    console.log('📊 Checking existing projects...');
    const { data: projects, error: projectError } = await directSupabase
      .from('projects')
      .select('id, customer_id, customer_name, customer_email')
      .not('customer_id', 'is', null);
    
    if (projectError) {
      throw new Error(`Failed to fetch projects: ${projectError.message}`);
    }
    
    console.log(`Found ${projects?.length || 0} projects with customer_id`);
    
    if (!projects || projects.length === 0) {
      console.log('ℹ️ No projects with customer_id found');
      return { success: true, updated: 0, message: 'No projects to update' };
    }
    
    // Step 3: Update project references
    let updatedCount = 0;
    const updateResults = [];
    
    for (const mapping of mappings) {
      try {
        // Try multiple approaches to match projects to customers:
        
        // Approach 1: Direct integer match (customer_id = external_id as integer)
        console.log(`🔍 Trying to update projects for ${mapping.customer_name} (External ID: ${mapping.quotedock_customer_id})`);
        
        const { data: directMatches, error: directError } = await directSupabase
          .from('projects')
          .update({ customer_id: mapping.jarvis_customer_id })
          .eq('customer_id', mapping.quotedock_customer_id)
          .select('id, customer_name');
        
        if (directError) {
          console.error(`❌ Database error updating projects for ${mapping.customer_name}:`, directError);
          updateResults.push({
            customer: mapping.customer_name,
            method: 'direct_integer',
            count: 0,
            success: false,
            error: directError.message
          });
          continue;
        }
        
        if (directMatches && directMatches.length > 0) {
          updatedCount += directMatches.length;
          console.log(`✅ Updated ${directMatches.length} projects for ${mapping.customer_name} (direct integer match)`);
          updateResults.push({
            customer: mapping.customer_name,
            method: 'direct_integer',
            count: directMatches.length,
            success: true
          });
          continue;
        }
        
        // Approach 2: String match (customer_id = "123" for external_id 123)
        const { data: stringMatches, error: stringError } = await directSupabase
          .from('projects')
          .update({ customer_id: mapping.jarvis_customer_id })
          .eq('customer_id', mapping.quotedock_customer_id.toString())
          .select('id, customer_name');
        
        if (stringMatches && stringMatches.length > 0) {
          updatedCount += stringMatches.length;
          console.log(`✅ Updated ${stringMatches.length} projects for ${mapping.customer_name} (string match)`);
          updateResults.push({
            customer: mapping.customer_name,
            method: 'string',
            count: stringMatches.length,
            success: true
          });
          continue;
        }
        
        // Approach 3: Match by customer email if available
        if (mapping.customer_email) {
          const { data: emailMatches, error: emailError } = await directSupabase
            .from('projects')
            .update({ customer_id: mapping.jarvis_customer_id })
            .eq('customer_email', mapping.customer_email)
            .select('id, customer_name');
          
          if (emailMatches && emailMatches.length > 0) {
            updatedCount += emailMatches.length;
            console.log(`✅ Updated ${emailMatches.length} projects for ${mapping.customer_name} (email match)`);
            updateResults.push({
              customer: mapping.customer_name,
              method: 'email',
              count: emailMatches.length,
              success: true
            });
            continue;
          }
        }
        
        // Approach 4: Match by customer name (fuzzy)
        const { data: nameMatches, error: nameError } = await directSupabase
          .from('projects')
          .update({ customer_id: mapping.jarvis_customer_id })
          .ilike('customer_name', `%${mapping.customer_name}%`)
          .select('id, customer_name');
        
        if (nameMatches && nameMatches.length > 0) {
          updatedCount += nameMatches.length;
          console.log(`✅ Updated ${nameMatches.length} projects for ${mapping.customer_name} (name match)`);
          updateResults.push({
            customer: mapping.customer_name,
            method: 'name',
            count: nameMatches.length,
            success: true
          });
          continue;
        }
        
        // No matches found
        console.log(`⚠️ No projects found for customer: ${mapping.customer_name} (ID: ${mapping.quotedock_customer_id})`);
        updateResults.push({
          customer: mapping.customer_name,
          method: 'none',
          count: 0,
          success: false,
          reason: 'No matching projects found'
        });
        
      } catch (error) {
        console.error(`❌ Error updating projects for ${mapping.customer_name}:`, error);
        updateResults.push({
          customer: mapping.customer_name,
          method: 'error',
          count: 0,
          success: false,
          reason: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }
    
    // Step 4: Verify results
    console.log('📊 Verification: Checking updated project-customer relationships...');
    const { data: updatedProjects, error: verifyError } = await directSupabase
      .from('projects')
      .select('id, customer_id, customer_name')
      .not('customer_id', 'is', null);
    
    if (!verifyError && updatedProjects) {
      const projectsWithUUIDs = updatedProjects.filter(p => 
        p.customer_id && p.customer_id.length === 36 && p.customer_id.includes('-')
      );
      
      console.log(`✅ Verification: ${projectsWithUUIDs.length} projects now have UUID customer_id references`);
    }
    
    // Summary
    console.log('✅ Project reference fix completed!');
    console.log(`📊 Summary:`);
    console.log(`   - Total mappings processed: ${mappings.length}`);
    console.log(`   - Projects updated: ${updatedCount}`);
    console.log(`   - Update methods used:`);
    
    const methodCounts = updateResults.reduce((acc, result) => {
      acc[result.method] = (acc[result.method] || 0) + result.count;
      return acc;
    }, {} as Record<string, number>);
    
    Object.entries(methodCounts).forEach(([method, count]) => {
      if (count > 0) {
        console.log(`     - ${method}: ${count} projects`);
      }
    });
    
    return {
      success: true,
      totalMappings: mappings.length,
      projectsUpdated: updatedCount,
      updateResults,
      methodCounts
    };
    
  } catch (error) {
    console.error('❌ Project reference fix failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// Helper function to run from browser console
(window as any).fixProjectReferences = fixProjectCustomerReferences;
